import React, { useMemo, useState } from 'react'
import NavbarLayout from '../../../components/layout/NavbarLayout/NavbarLayout'
import './CoachPage.scss'
import SoloCollapse from '../../../components/ui/SoloCollapse/SoloCollapse';
import PageTitle from '../../../components/common/PageTitle/PageTitle';
import {
    ColumnsType,
} from 'antd/lib/table/interface';
import FormDropDescription from '../../../components/table/TableDropDescription/TableDropDescription';
import TableObjects from '../../../components/ui/TableObjects/TableObjects';
import { useTableObject } from '../../../utils/hooks/useTableObject';
import { addCollapseIcon, editCollapseIcon } from '../../../components/ui/SoloCollapse/components/icons';
import TableActionButtons from '../../../components/ui/TableObjects/components/TableActionButtons/TableActionButtons';
import { useMutation, useQuery } from 'react-query';
import { CoachObj, ObjDB, UploadObj } from '../../../types/apiTypes';
import { api } from '../../../api/api';
import { getSelectedItems } from '../../../utils/parsers';
import { openNotification } from '../../../utils/jsxUtils';
import { useParams } from 'react-router';
import CoachAddForm from './components/CoachAddForm/CoachAddForm';
import TableImage from '../../../components/table/TableImage/TableImage';
import CoachEditForm from './components/CoachEditForm/CoachEditForm';
import { invalidateQueries, queryOptionsPreset } from '../../../utils/rest';
import { queryKeysCoach } from '../../../static/data/queryKeys';
import useScrollMount from '../../../utils/hooks/useScrollMount';


type TableItem = ObjDB<CoachObj>

interface CoachPageProps {}

const CoachPage: React.FC<React.PropsWithChildren<CoachPageProps>> = () => {

    const [openedAddForm, setOpenedAddForm] = useState<boolean>(false)
    const [openedEditForm, setOpenedEditForm] = useState<boolean>(false)
    const [editItem, setEditItem] = useState<TableItem | null>(null)
    const [tableHeadLoader, setTableHeadLoader] = useState<boolean>(false)
    const [avatarItems, setAvatarItems] = useState<ObjDB<UploadObj>[]>([])

    const params = useParams<{ id: string }>()

    const {
        selectedRowKeys,
        searchText,
        setSelectedRowKeys,
        getColumnSearchProps,
    } = useTableObject({initialSearchText: params.id})

    const queryAll = useQuery<undefined, Error, TableItem[]>(
        queryKeysCoach[0],
        api.coaches.getAll,
        {
            onError(err: Error) {
                console.log('ERROR get all objects', err)
                openNotification(
                    'error',
                    'Ошибка получения списка объектов',
                    err.message
                )
            },
            ...queryOptionsPreset,
        }
    )

    const queryDelete = useMutation(
        'coach-delete',
        api.coaches.delete,
        {
            onSuccess() {
                openNotification('success', 'Объект успешно удален')
                invalidateQueries('coach-all')
            },
            onError(err: Error) {
                console.error("Error delete object: ", err.message)
                openNotification('error', 'Ошибка при удалении объекта', err.message)
            },
        } as any
    )

    const avatarMutate = useMutation(
        'avatar-mutate',
        api.file.get,
        {
            onSuccess(data: ObjDB<UploadObj>) {
                setAvatarItems([...avatarItems, data])
            },
            onError(err: Error) {
                console.error("Error load object: ", err.message)
                openNotification('error', 'Ошибка при загрузке файла', err.message)
            },
        } as any
    )
    //ColumnsType<TableItem>
    const columns: any = useMemo(() => {
        return [
            {
                title: 'ID',
                dataIndex: 'id',
                sorter: { compare: (a, b) => a.id - b.id },
                ...getColumnSearchProps('id'),
                defaultFilteredValue: params.id === searchText ? [params.id] : undefined,
                width: 90,
            },
            {
                title: 'Имя',
                dataIndex: 'first_name',
                sorter: { compare: (a, b) => a.first_name.localeCompare(b.first_name) },
                ...getColumnSearchProps('first_name'),
            },
            {
                title: 'Фамилия',
                dataIndex: 'last_name',
                sorter: { compare: (a, b) => a.last_name.localeCompare(b.last_name) },
                ...getColumnSearchProps('last_name'),
            },
            {
                title: 'ID Фото',
                dataIndex: 'avatar_id',
                sorter: { compare: (a, b) => a.avatar_id - b.avatar_id },
                ...getColumnSearchProps('avatar_id'),
                render: (id: number) => (
                    <TableImage
                        id={id}
                        imgContainer={avatarItems}
                        isLoading={avatarMutate.isLoading}
                        onMutate={id => avatarMutate.mutate(id)}
                    />
                )
            },
            {
                title: 'Уровень',
                dataIndex: 'quality',
                sorter: { compare: (a, b) => a.quality.localeCompare(b.quality) },
                ...getColumnSearchProps('quality'),
            },
            {
                title: 'Действие',
                dataIndex: '',
                align: 'right',
                render: (value: TableItem) => (
                    <TableActionButtons
                        onEdit={() => handleEditItem(value)}
                        onDelete={() => queryDelete.mutate(value)}
                    />
                )
            },
        ]
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getColumnSearchProps])

    const onSelectChange = (selectedRowKeys: React.Key[]) => {
        setSelectedRowKeys(selectedRowKeys)
    }

    const handleTableAdd = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' })
        setOpenedAddForm(true)
    }

    const handleEditItem = (value: TableItem) => {
        setEditItem(value)
        setOpenedEditForm(true)
        setOpenedAddForm(false)
        window.scrollTo({ top: 0, behavior: 'smooth' })
    }
    const handleTableDelete = async () => {
        try {
            setTableHeadLoader(true)
            const deleteItems = getSelectedItems<TableItem>(selectedRowKeys, queryAll.data || [])
            await api.coaches.deleteMany(deleteItems)
            invalidateQueries(queryKeysCoach[0])
            openNotification(
                'success',
                'Выбранные объекты успешно удалены'
            )
            setSelectedRowKeys([])
        } catch (err: any) {
            console.error('Error delete many', err)
            openNotification(
                'error',
                'Ошибка при удалении объектов',
                err.message
            )
        } finally { setTableHeadLoader(false) }
    }

    const handleTableRefresh = () => invalidateQueries(queryKeysCoach)
    useScrollMount()

    return (
        <NavbarLayout>
            <section className="uif-coach-page">
                <PageTitle text="Преподаватели | Coaches" />

                <div className="uif-coach-page__collapses">
                    <SoloCollapse
                        header="Создать новый объект"
                        keyCollapse="add"
                        show={openedAddForm}
                        icon={addCollapseIcon}
                        setShow={setOpenedAddForm}
                    >
                        <CoachAddForm />
                    </SoloCollapse>
                    <SoloCollapse
                        header="Редактировать объект"
                        keyCollapse="edit"
                        show={openedEditForm}
                        icon={editCollapseIcon}
                        setShow={setOpenedEditForm}

                    >
                        <CoachEditForm
                            editItem={editItem}
                            setOpenedForm={setOpenedEditForm}
                            setEditItem={setEditItem}
                        />
                    </SoloCollapse>
                </div>

                <TableObjects
                    selectedRowKeys={selectedRowKeys}
                    count={queryAll.data?.length || 0}
                    columns={columns}
                    dataSource={queryAll.data}
                    rowSelection={{
                        selectedRowKeys: selectedRowKeys,
                        onChange: onSelectChange
                    }}
                    expandable={{
                        expandedRowRender: (record: TableItem) => (
                            <FormDropDescription
                                data={[
                                    { title: 'Опыт работы', text: record.experience || '' },
                                    { title: 'Образование', text: record.education || '' },
                                ]}
                            />
                        ),
                    }}
                    onDelete={handleTableDelete}
                    onAdd={handleTableAdd}
                    onRefresh={handleTableRefresh}
                    rowKey="id"
                    isFetching={queryAll.isFetching}
                    headerLoader={tableHeadLoader}
                    scroll={{x: 800}}
                />
            </section>
        </NavbarLayout >
    )
}

export default CoachPage